import type { CSSProperties, ReactElement, ReactNode, RefObject } from 'react';
import { useEffect } from 'react';
import { Tooltip } from 'antd';
import type { InputProps, SelectProps } from 'antd';

import { colorConstants } from 'config/colorConstants';
import type { DatePickerStartWeekday } from 'utils/Calender';

export type OptionValue = string | number;

export type GenericFormControlOption = {
    label: ReactNode;
    value: OptionValue;
    disabled?: boolean;
};

export type TooltipAwareProps = {
    showTooltip?: boolean;
    tooltipText?: ReactNode;
    staticTooltipText?: ReactNode;
};

export type AllowedValueType = 'any' | 'alphabet' | 'number';
export type DecimalPlaces = 1 | 2;

export type InputValidationProps = {
    allowedValueType?: AllowedValueType;
    allowDecimal?: boolean;
    decimalPlaces?: DecimalPlaces;
};

export type GenericSharedDropdownControlProps = TooltipAwareProps & {
    options: GenericFormControlOption[];
    value?: OptionValue;
    onChange: (value: OptionValue) => void;
    placeholder?: string;
    disabled?: boolean;
    allowClear?: boolean;
    className?: string;
    style?: CSSProperties;
    size?: SelectProps<OptionValue>['size'];
};

export type GenericDropdownControlProps = GenericSharedDropdownControlProps & {
    labelPrefix?: string;
};

export type GenericSearchableDropdownControlProps = GenericSharedDropdownControlProps;

export type GenericInputBoxControlProps = TooltipAwareProps &
    InputValidationProps &
    Omit<InputProps, 'value' | 'onChange'> & {
        value: string;
        onChange: (value: string) => void;
        showSave?: boolean;
        onSave?: (value: string) => void;
        saveButtonText?: string;
        containerClassName?: string;
    };

export type GenericPrefixedInputBoxControlProps = TooltipAwareProps &
    InputValidationProps &
    Omit<InputProps, 'value' | 'onChange' | 'addonBefore' | 'prefix'> & {
        prefixText?: string;
        value: string;
        onChange: (value: string) => void;
        showSave?: boolean;
        onSave?: (value: string) => void;
        saveButtonText?: string;
        containerClassName?: string;
    };

export type GenericDatePickerControlProps = TooltipAwareProps & {
    value?: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    style?: CSSProperties;
    startWeekday?: DatePickerStartWeekday;
    minYear?: number;
    maxYear?: number;
};

export type GenericCheckboxControlProps = TooltipAwareProps & {
    checked?: boolean;
    indeterminate?: boolean;
    /** When true, shows the dash (-) for partial selection. Default false — unchecked stays empty. */
    showIndeterminate?: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    className?: string;
    /** Optional outer wrapper — use for row hover/selected background (full-width list rows). */
    wrapperClassName?: string;
    /** When true and checked, label uses purple + medium weight (lens config selected row). Row bg stays on wrapper. */
    highlightLabelWhenChecked?: boolean;
    children?: ReactNode;
    id?: string;
    dataTestId?: string;
    ariaLabel?: string;
    /** When true, stops the click/mousedown from bubbling — useful inside dropdowns that close on outside click. */
    stopPropagation?: boolean;
    /**
     * Checked item that cannot be unchecked — auto-disables, Figma Active/Disabled fill, grey tick.
     * Shows tooltipText/staticTooltipText on hover when set (no showTooltip needed).
     */
    locked?: boolean;
};

export type GenericCheckboxTooltipItem = {
    label: string;
    value: string;
};

export type GenericCheckboxTooltipListProps = {
    items: GenericCheckboxTooltipItem[];
};

export type WeekdayDatePickerPanelProps = {
    selectedStart: Date | null;
    selectedEnd: Date | null;
    visibleYear: number;
    yearOptions: number[];
    weekdayIndex: number;
    onYearChange: (year: number) => void;
    onSelectDate: (date: Date) => void;
};

export type BaseDropdownControlProps = GenericSharedDropdownControlProps & {
    searchable?: boolean;
    labelPrefix?: string;
    searchPlaceholder?: string;
};

export const BaseInputBaseClassName = '!h-8 !rounded-[6px] !border !shadow-none focus:!shadow-none';

export const BaseControlTypography: CSSProperties = {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '22px',
};

export const BaseActionButtonClassName =
    '!inline-flex !size-6 !min-w-0 !items-center !justify-center !rounded-[4px] !border !p-0 !leading-none';

export const BaseActionButtonsContainerClassName =
    '!absolute !right-0 !top-[calc(100%+6px)] !z-10 flex items-center gap-1';

export const BaseActionButtonsContainerStyle: CSSProperties = {
    filter: `drop-shadow(0px 3px 3px ${colorConstants.blackTransparent12}) drop-shadow(0px 9px 14px ${colorConstants.blackTransparent05})`,
};

export const BaseActionButtonOutlineShadow = `0 2px 0 0 ${colorConstants.tableHeaderHoverBg}`;

export const BaseActionButtonStyle: CSSProperties = {
    boxShadow: BaseActionButtonOutlineShadow,
};

export const BaseDropdownMenuShadow = `0px 6px 8px ${colorConstants.blackTransparent08}, 0px 3px 3px ${colorConstants.blackTransparent12}, 0px 9px 14px ${colorConstants.blackTransparent05}`;

export const BaseCancelActionButtonStyle: CSSProperties = {
    ...BaseActionButtonStyle,
    borderColor: colorConstants.toolbarBorderGrey,
    background: colorConstants.white,
    color: colorConstants.axisLabelGrey,
};

export const BaseConfirmActionButtonStyle: CSSProperties = {
    ...BaseActionButtonStyle,
    borderColor: colorConstants.toolbarBorderGrey,
    background: colorConstants.white,
    color: colorConstants.purpleFontSelected,
};

export const DATE_PICKER_PANEL_SHADOW = `0px 3px 3px ${colorConstants.blackTransparent12}, 0px 9px 14px ${colorConstants.blackTransparent05}`;

export const DATE_PICKER_DROPDOWN_OVERLAY_STYLE = {
    padding: 0,
    background: 'transparent',
    boxShadow: 'none',
};

export const CHECKBOX_TOOLTIP_OVERLAY_STYLE = {
    padding: 0,
    background: 'transparent',
    boxShadow: 'none',
    maxWidth: 'none',
};

/** Dark hint tooltip — Figma locked checkbox (placement top, arrow centered on checkbox). */
export const LOCKED_CHECKBOX_TOOLTIP_PROPS = {
    color: colorConstants.tooltipBg,
    placement: 'top' as const,
    align: { offset: [0, 8] },
    overlayStyle: { width: 140, minWidth: 140 },
    overlayInnerStyle: {
        borderRadius: 6,
        fontSize: 14,
        fontWeight: 400,
        paddingBlock: 6,
        paddingInline: 8,
    },
};

export const CHECKBOX_TOOLTIP_SHADOW = `0px 6px 16px ${colorConstants.blackTransparent12}, 0px 3px 6px ${colorConstants.blackTransparent12}, 0px 9px 28px ${colorConstants.blackTransparent05}`;

export const renderWithOptionalTooltip = (
    control: ReactElement,
    showTooltip: boolean,
    tooltipText?: ReactNode,
): ReactElement => {
    if (!showTooltip || tooltipText == null || tooltipText === '') return control;
    return (
        <Tooltip title={tooltipText} destroyTooltipOnHide>
            <div className="inline-block w-full">{control}</div>
        </Tooltip>
    );
};

export const resolveTooltipText = (
    staticTooltipText: ReactNode | undefined,
    tooltipText: ReactNode | undefined,
    fallbackText: ReactNode | undefined,
): ReactNode | undefined => {
    if (staticTooltipText !== undefined && staticTooltipText !== null) return staticTooltipText;
    if (tooltipText !== undefined && tooltipText !== null) return tooltipText;
    return fallbackText;
};

export const getOptionLabelText = (label: ReactNode): string => {
    if (typeof label === 'string' || typeof label === 'number') return String(label);
    return '';
};

export const buildInputStyle = (style?: CSSProperties): CSSProperties => ({
    color: colorConstants.blackTitle,
    borderColor: colorConstants.inputBorderMuted,
    background: colorConstants.white,
    boxShadow: 'none',
    ...BaseControlTypography,
    ...style,
});

export const sanitizeInputValue = (
    rawValue: string,
    allowedValueType: AllowedValueType,
    allowDecimal: boolean,
    decimalPlaces?: DecimalPlaces,
): string => {
    if (allowedValueType === 'alphabet') {
        return rawValue.replace(/[^a-zA-Z\s]/g, '');
    }

    if (allowedValueType === 'number') {
        if (!allowDecimal) {
            return rawValue.replace(/[^0-9]/g, '');
        }

        const cleaned = rawValue.replace(/[^0-9.]/g, '');
        const [integerPart, ...decimalParts] = cleaned.split('.');
        if (!decimalParts.length) return integerPart;
        const mergedDecimals = decimalParts.join('');
        const boundedDecimals =
            typeof decimalPlaces === 'number' ? mergedDecimals.slice(0, decimalPlaces) : mergedDecimals;
        return `${integerPart}.${boundedDecimals}`;
    }

    return rawValue;
};

export const padTwoDigitDatePart = (value: number) => String(value).padStart(2, '0');

export const DATE_PICKER_RANGE_SEPARATOR = ' - ';

export const formatDatePickerValue = (date: Date) => {
    const year = date.getFullYear();
    const yearPart = year >= 2000 && year <= 2099 ? padTwoDigitDatePart(year % 100) : String(year);
    return `${padTwoDigitDatePart(date.getDate())}/${padTwoDigitDatePart(date.getMonth() + 1)}/${yearPart}`;
};

export const formatDatePickerRangeValue = (start: Date, end: Date) =>
    `${formatDatePickerValue(start)}${DATE_PICKER_RANGE_SEPARATOR}${formatDatePickerValue(end)}`;

export const toDateFromTimestamp = (value: number): Date | null => {
    if (!Number.isFinite(value)) return null;
    const milliseconds = value > 1e12 ? value : value * 1000;
    const date = new Date(milliseconds);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const parseDatePickerValue = (value?: string): Date | null => {
    if (!value?.trim()) return null;

    const trimmed = value.trim();
    const [dayPart, monthPart, yearPart] = trimmed.split('/');
    const day = Number(dayPart);
    const month = Number(monthPart);
    const parsedYear = Number(yearPart);
    if (day && month && !Number.isNaN(parsedYear)) {
        const year = parsedYear < 100 ? 2000 + parsedYear : parsedYear;
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
            return date;
        }
    }

    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
        return toDateFromTimestamp(Number(trimmed));
    }

    const parsed = Date.parse(trimmed);
    if (!Number.isFinite(parsed)) return null;
    const date = new Date(parsed);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const formatUnknownDatePickerValue = (value: unknown): string => {
    if (value === undefined || value === null || value === '' || typeof value === 'boolean') return '';
    if (typeof value === 'number') {
        const date = toDateFromTimestamp(value);
        return date ? formatDatePickerValue(date) : String(value);
    }
    const raw = String(value).trim();
    if (!raw) return '';
    const range = parseDatePickerRangeValue(raw);
    if (range.start && range.end) return formatDatePickerRangeValue(range.start, range.end);
    if (range.start) return formatDatePickerValue(range.start);
    return raw;
};

export const parseDatePickerRangeValue = (value?: string): { start: Date | null; end: Date | null } => {
    if (!value?.trim()) return { start: null, end: null };
    const [startPart, endPart] = value.split(DATE_PICKER_RANGE_SEPARATOR).map((part) => part.trim());
    return {
        start: parseDatePickerValue(startPart),
        end: parseDatePickerValue(endPart),
    };
};

export const isDateInInclusiveRange = (date: Date, start: Date | null, end: Date | null) => {
    if (!start || !end) return false;
    const timestamp = date.getTime();
    return timestamp >= start.getTime() && timestamp <= end.getTime();
};

export const getDatePickerDayCellStyle = (isEndpoint: boolean, isInRange: boolean): CSSProperties => ({
    color: colorConstants.blackTitle,
    border: isEndpoint ? `1px solid ${colorConstants.violet}` : '1px solid transparent',
    background: isEndpoint
        ? colorConstants.white
        : isInRange
          ? colorConstants.tableSortIconBg
          : 'transparent',
});

export const getWeekdayDatesInMonth = (year: number, monthIndex: number, weekdayIndex: number) => {
    const dates: number[] = [];
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day += 1) {
        if (new Date(year, monthIndex, day).getDay() === weekdayIndex) dates.push(day);
    }

    return dates;
};

export const buildYearOptions = (minYear: number, maxYear: number) =>
    Array.from({ length: maxYear - minYear + 1 }, (_, index) => minYear + index);

export const isSameCalendarDate = (left: Date | null, right: Date) =>
    Boolean(
        left &&
            left.getFullYear() === right.getFullYear() &&
            left.getMonth() === right.getMonth() &&
            left.getDate() === right.getDate(),
    );

export const getResolvedInputMode = (
    providedInputMode: InputProps['inputMode'],
    allowedValueType: AllowedValueType,
    allowDecimal: boolean,
): InputProps['inputMode'] => {
    if (providedInputMode) return providedInputMode;
    if (allowedValueType !== 'number') return 'text';
    return allowDecimal ? 'decimal' : 'numeric';
};

export const useOutsideDismiss = (
    containerRef: RefObject<HTMLDivElement>,
    isOpen: boolean,
    onDismiss: () => void,
) => {
    useEffect(() => {
        if (!isOpen) return undefined;
        const handleOutsideClick = (event: MouseEvent) => {
            if (containerRef.current?.contains(event.target as Node)) return;
            onDismiss();
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [containerRef, isOpen, onDismiss]);
};
